import { MONTH_SHORT } from "./metricChange";

/** Rome days "YYYY-MM-DD", both included: the backend's span shape. */
export interface DayRange {
  since: string;
  until: string;
}

const DAY_MS = 86_400_000;

const ROME_TODAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Rome",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

// Every sum on UTC midnights, never local time, so a DST change inside a
// range neither drops nor doubles a day.
const toUtc = (iso: string): number => {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};

const fromUtc = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** Today in Europe/Rome, "YYYY-MM-DD". */
export const todayRome = (now: Date = new Date()): string => ROME_TODAY.format(now);

export const addDays = (iso: string, days: number): string => fromUtc(toUtc(iso) + days * DAY_MS);

/** The `days` days ending on `until`. */
export const rangeOf = (until: string, days: number): DayRange => ({
  since: addDays(until, -(days - 1)),
  until,
});

export const rangeLength = (range: DayRange): number =>
  Math.round((toUtc(range.until) - toUtc(range.since)) / DAY_MS) + 1;

/** The range of the same length ending the day before `range` starts. */
export const previousRange = (range: DayRange): DayRange =>
  rangeOf(addDays(range.since, -1), rangeLength(range));

/** The calendar month, the 1st through its last day. */
export const monthRange = (year: number, month: number): DayRange => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    since: `${year}-${pad(month)}-01`,
    until: `${year}-${pad(month)}-${pad(new Date(Date.UTC(year, month, 0)).getUTCDate())}`,
  };
};

/** Every day of the range, ascending. */
export const rangeDays = (range: DayRange): string[] =>
  Array.from({ length: Math.max(rangeLength(range), 0) }, (_, i) => addDays(range.since, i));

/** The day's place on the range's axis, from 0; null outside the range. */
export const dayIndex = (range: DayRange, iso: string): number | null => {
  const day = iso.slice(0, 10);
  if (day < range.since || day > range.until) return null;
  return Math.round((toUtc(day) - toUtc(range.since)) / DAY_MS);
};

/** The indexes a day axis of `length` days labels: the first and last day
 *  and every 10th between on a month-long axis (days 1, 10, 20), every 30th
 *  on a longer one, the middle on a week; a tick too close to the last is
 *  dropped. */
export const axisTicks = (length: number): number[] => {
  const last = Math.max(length - 1, 0);
  if (length <= 10) return Array.from(new Set([0, Math.floor(last / 2), last]));
  const step = length <= 31 ? 10 : 30;
  const ticks = [0];
  for (let t = step - 1; last - t >= step / 2; t += step) ticks.push(t);
  return [...ticks, last];
};

/** The calendar months the range touches, oldest first. */
export const monthsTouched = (range: DayRange): Array<{ year: number; month: number }> => {
  const months: Array<{ year: number; month: number }> = [];
  let [year, month] = range.since.split("-").map(Number);
  const [lastYear, lastMonth] = range.until.split("-").map(Number);
  while (year < lastYear || (year === lastYear && month <= lastMonth)) {
    months.push({ year, month });
    [year, month] = month === 12 ? [year + 1, 1] : [year, month + 1];
  }
  return months;
};

/** "3 Oct" from "2026-10-03", read from the string so no time zone moves it. */
export const formatShortDay = (isoDate: string): string => {
  const [, month, day] = isoDate.slice(0, 10).split("-").map(Number);
  return `${day} ${MONTH_SHORT[month - 1]}`;
};

/** A campaign's days: "3–17 Oct", "28 Sep – 4 Oct", or "since 3 Oct" while
 *  it runs. */
export const formatCampaignDates = (startDate: string, endDate: string | null): string => {
  if (!endDate) return `since ${formatShortDay(startDate)}`;
  if (startDate === endDate) return formatShortDay(startDate);
  if (startDate.slice(0, 7) === endDate.slice(0, 7)) {
    return `${Number(startDate.slice(8, 10))}–${formatShortDay(endDate)}`;
  }
  return `${formatShortDay(startDate)} – ${formatShortDay(endDate)}`;
};

/** "3–17 Oct", "28 Sep – 4 Oct". */
export const formatRange = (range: DayRange): string => formatCampaignDates(range.since, range.until);

/** A window beside the one before it: "6 Oct – 2 Nov vs 8 Sep – 5 Oct". */
export const formatRanges = (current: DayRange, previous: DayRange | null): string =>
  previous ? `${formatRange(current)} vs ${formatRange(previous)}` : formatRange(current);
